import React, { useState, useEffect, useRef } from 'react';
import { Project } from '../types';
import { Search, X, Calendar, Tag, ArrowRight, Layout, Loader2 } from 'lucide-react';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
  onSelect: (project: Project) => void;
}

interface SearchResultItemProps {
  project: Project;
  onClick: (project: Project) => void;
}

// Sub-component for individual search result items to handle their own image loading state
const SearchResultItem: React.FC<SearchResultItemProps> = ({ project, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  
  // Use saved thumbnail directly
  const previewUrl = project.thumbnailUrl;

  return (
    <button
      onClick={() => onClick(project)}
      className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-surfaceLight/50 hover:border-white/5 border border-transparent transition-all group text-left"
    >
      {/* Thumbnail with loading state */}
      <div className="w-12 h-10 rounded-md bg-background overflow-hidden shrink-0 border border-white/10 shadow-sm relative">
        {!loaded && (
           <div className="absolute inset-0 flex items-center justify-center bg-surfaceLight animate-pulse">
             <Loader2 size={10} className="text-slate-500 animate-spin" />
           </div>
        )}
        <img 
            src={previewUrl} 
            alt="" 
            className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-80 group-hover:opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).src = 'https://picsum.photos/150/100?grayscale'; setLoaded(true); }}
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-medium text-slate-200 group-hover:text-primaryLight truncate">
              {project.title}
            </h4>
            <span className="text-[9px] px-1.5 py-0 bg-primary/10 rounded-full text-primary border border-primary/20">
              {project.category}
            </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-textMuted">
            <span className="flex items-center gap-1">
              <Calendar size={10} />
              {new Date(project.createdAt).toLocaleDateString()}
            </span>
        </div>
      </div>

      {/* Action Icon */}
      <ArrowRight size={16} className="text-textMuted group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
    </button>
  );
};

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose, projects, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Project[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setResults(projects);
      setQuery('');
    }
  }, [isOpen, projects]);

  useEffect(() => {
    if (!query.trim()) {
      setResults(projects);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = projects.filter(p => {
      const matchTitle = p.title.toLowerCase().includes(lowerQuery);
      const matchCategory = p.category.toLowerCase().includes(lowerQuery);
      const matchTags = p.tags.some(t => t.toLowerCase().includes(lowerQuery));
      const dateStr = new Date(p.createdAt).toLocaleDateString().toLowerCase();
      const matchDate = dateStr.includes(lowerQuery);
      
      return matchTitle || matchCategory || matchTags || matchDate;
    });

    setResults(filtered);
  }, [query, projects]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-surface border border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[60vh] animate-slide-up ring-1 ring-white/5 shadow-primary/5">
        
        {/* Search Header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-white/5 bg-surfaceLight/20">
          <Search className="text-primary" size={20} />
          <input 
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent border-none outline-none text-white placeholder:text-textMuted text-sm h-full"
            placeholder="Search by name, date, category or tag..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'Enter' && results.length > 0) {
                 onSelect(results[0]);
              }
            }}
          />
          <button onClick={onClose} className="p-1.5 text-textMuted hover:text-white hover:bg-white/10 rounded-lg transition-colors">
             <X size={16} />
          </button>
        </div>

        {/* Results List */}
        <div className="flex-1 overflow-y-auto p-2 scrollbar-thin">
          {results.length === 0 ? (
            <div className="py-12 text-center text-textMuted text-sm flex flex-col items-center gap-2 opacity-50">
              <Search size={32} />
              <span>No projects found for "{query}"</span>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="px-3 py-2 text-[10px] text-primary/70 uppercase tracking-widest font-semibold">
                {query ? 'Search Results' : 'Recent'}
              </div>
              {results.map((project) => (
                <SearchResultItem 
                  key={project.id} 
                  project={project} 
                  onClick={onSelect} 
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Footer Hints */}
        <div className="px-4 py-2 bg-black/20 border-t border-white/5 text-[10px] text-textMuted flex justify-between items-center">
           <div className="flex gap-4 opacity-70">
              <span><strong className="text-slate-300">↑↓</strong> navigate</span>
              <span><strong className="text-slate-300">Enter</strong> open</span>
              <span><strong className="text-slate-300">Esc</strong> close</span>
           </div>
           <span className="opacity-50">{results.length} found</span>
        </div>
      </div>
    </div>
  );
};